import React, { useEffect, useState } from 'react'
import { cloneDeep, isEqual } from 'lodash'
import { Button, Icon } from 'semantic-ui-react'

import ToastService from 'services/toast.service'
import SaveService from 'services/save.service'

import { useActionless, useAppSelector } from 'redux/hooks'
import { CardType, defaultCard, Finishes } from 'types/deck'
import CardForm from 'components/card/cardForm/cardForm'

import CursorCardWrapper from 'components/card/CursorCardWrapper'
import GlobalOverlayStack from 'components/elements/Overlay/GlobalOverlayStack'

import styles from './multiSelectEditingOverlay.module.scss'

import { SET_ACTIVE_STATE } from 'redux/active/actions/types'

type Props = {
  open: boolean
  onClose: () => void
}

type CategoryOption = 'overwrite' | 'append-to-front' | 'append-to-end'
const categoryOverwriteOptions: Array<{ label: string; value: CategoryOption }> = [
  { label: 'Overwrite Categories', value: 'overwrite' },
  { label: 'Use new primary (leaves existing categories)', value: 'append-to-front' },
  { label: 'Use existing primary (leaves existing categories)', value: 'append-to-end' },
]
const MultiSelectEditingOverlay = ({ open, onClose }: Props) => {
  const cardMap = useAppSelector(state => state.deck.cardMap)
  const multiSelectedIds = useAppSelector(state => state.active.multiSelectedIds)

  const [setActiveState] = useActionless(SET_ACTIVE_STATE)

  const [edits, setEdits] = useState<Partial<CardType>>({})
  const [editedCard, setEditedCard] = useState<CardType>(cloneDeep({ ...defaultCard }))
  const [categoryOption, setCategoryOption] = useState<CategoryOption>('overwrite')

  const selectedCards = Object.keys(multiSelectedIds).reduce((acc, id) => {
    const card = cardMap[id]

    if (card) acc.push(card)

    return acc
  }, new Array<CardType>())

  const handleCardChange = (updatedCard: CardType) => {
    // This flag is used because the card form sets the quantity of a card to 1 when a category is changed
    // This is to automatically "add" a card to the deck when you set a category
    // Here however, we _only_ want the category changes
    // This flag is used to ignore the quantity changes if/when categories have been changed
    let categoriesChanged = false

    const differentKeys = (Object.keys(editedCard) as Array<keyof CardType>).reduce((acc, key: keyof CardType) => {
      const oldValue = editedCard[key]
      const newValue = updatedCard[key]

      if (!isEqual(oldValue, newValue)) acc.push(key)

      return acc
    }, new Array<keyof CardType>())

    const updates: Partial<CardType> = {}

    if (differentKeys.includes('categories')) categoriesChanged = true

    for (const key of differentKeys) {
      if (key === 'qty' && categoriesChanged) continue

      // @ts-ignore - I'd like to fix this
      updates[key] = updatedCard[key]
    }

    setEditedCard({ ...updatedCard, qty: categoriesChanged ? editedCard.qty : updatedCard.qty })
    setEdits({ ...edits, ...updates })
  }

  const handleSave = () => {
    const [updatedCards, removedCardIds] = selectedCards
      .map(card => ({
        ...card,
        ...edits,

        // Unchanged categories > Overwritten categories > appended to front > appended to back
        categories: !edits.categories
          ? card.categories
          : categoryOption === 'overwrite'
          ? edits.categories
          : categoryOption === 'append-to-front'
          ? [...edits.categories, ...card.categories.filter(name => !edits.categories?.includes(name))]
          : [...card.categories.filter(name => !edits.categories?.includes(name)), ...edits.categories],

        // Only allow the mass updated modifier (foiling) to be set IF the selected modifier is availible for the card in question
        modifier: edits.modifier && card.options.includes(edits.modifier) ? edits.modifier : card.modifier,
      }))
      .reduce(
        (acc, card) => {
          if (card.qty) acc[0].push(card)
          else acc[1].push(card.id)

          return acc
        },
        [new Array<CardType>(), new Array<string>()],
      )

    SaveService.save(updatedCards, removedCardIds)
      .then(() => {
        onClose()
        setActiveState({ multiSelectedIds: {} })
        setCategoryOption('overwrite')
      })
      .catch(() => ToastService.create('Failed to edit cards', 'Multi Edit', 'error'))
  }

  const resetEdits = () => {
    const availableOptions = selectedCards.reduce((acc, card) => {
      acc.push(...card.options)

      return [...new Set(acc)]
    }, new Array<Finishes>())

    // Setting colorLabel manually here so that it has a value it can change from => to to trigger this form knowing to update
    setEditedCard(cloneDeep({ ...defaultCard, options: availableOptions, colorLabel: { name: '', color: '' } }))
    setEdits({})
  }

  const handleRemoveSelection = (cardId: string) => {
    const updatedMultiSelectedIds = { ...multiSelectedIds }

    delete updatedMultiSelectedIds[cardId]

    setActiveState({ multiSelectedIds: updatedMultiSelectedIds })
  }

  const handleCategoryOptionsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // @ts-expect-error
    setCategoryOption(e.target.value)
  }

  useEffect(() => {
    // If the number of multi-selected cards falls to 0, reset all edits (aka dump any current edits)
    if (!Object.keys(multiSelectedIds).length) resetEdits()
    else {
      // Updating finishes for multi-edits. This allows a user to mark any finish that would be availible on any selected card
      // @see - handleSave for why we can get away with this
      const availableOptions = selectedCards.reduce((acc, card) => {
        acc.push(...card.options)

        return [...new Set(acc)]
      }, new Array<Finishes>())

      setEditedCard({ ...editedCard, options: availableOptions })
    }
  }, [multiSelectedIds])

  return (
    <GlobalOverlayStack open={open} onClose={onClose}>
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.cards}>
            <table className={styles.table}>
              <tbody>
                <tr>
                  <th />
                  <th>Name</th>
                  <th className={styles.mobileHidden}>Printing</th>
                </tr>
                {selectedCards.map(card => (
                  <tr key={card.id}>
                    <td className={styles.removeButton}>
                      <button onClick={() => handleRemoveSelection(card.id)}>
                        <Icon name="close" />
                      </button>
                    </td>

                    <td className={styles.names}>
                      <CursorCardWrapper key={card.id} card={card}>
                        {card.name}
                      </CursorCardWrapper>
                    </td>

                    <td className={styles.mobileHidden}>
                      {card.set} ({card.collectorNumber})
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className={styles.form}>
            <CardForm multiEdit card={editedCard} onCardUpdated={handleCardChange} />

            <div className={styles.categoryOptions}>
              {categoryOverwriteOptions.map(option => (
                <span key={option.value}>
                  <input
                    onChange={handleCategoryOptionsChange}
                    name={option.value}
                    value={option.value}
                    type="radio"
                    id={option.value}
                    checked={option.value === categoryOption}
                  />
                  <label htmlFor={option.value}>{option.label}</label>
                </span>
              ))}
            </div>
            <Button
              basic={!Object.keys(edits).length}
              disabled={!Object.keys(edits).length}
              onClick={handleSave}
              color="green"
              fluid>
              Apply Changes
            </Button>
          </div>
        </div>
      </div>
    </GlobalOverlayStack>
  )
}

export default MultiSelectEditingOverlay
